import type { FC } from 'react';
import React, { useEffect } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useFetchSyncBlockData } from '@atlaskit/editor-synced-block-provider/useFetchSyncBlockData';
import { type SyncBlockStoreManager } from '@atlaskit/editor-synced-block-provider/syncBlockStoreManager';

import type { SyncBlockReference } from './handleADF.ts';

export type SyncBlockTocEntry = {
	// The synced block reference node's `localId` — also the heading-id prefix.
	key: string;
	content: ADFEntity[];
};

type SubscriberProps = {
	manager: SyncBlockStoreManager;
	reference: SyncBlockReference;
	onResolve: (entry: SyncBlockTocEntry) => void;
};

/**
 * Subscribes to a single synced block instance via the shared
 * `useFetchSyncBlockData` hook. Because the store manager shares a module-level
 * in-memory session cache with the reference-block renderer, this reuses data
 * the renderer already fetched (no extra request) and receives live updates when
 * the source block changes.
 */
const SyncBlockTocSubscriber: FC<SubscriberProps> = ({ manager, reference, onResolve }) => {
	const { syncBlockInstance, isLoading } = useFetchSyncBlockData(
		manager,
		reference.resourceId,
		reference.localId,
	);

	useEffect(() => {
		if (isLoading) {
			return;
		}
		// On error / deleted / unpublished the instance carries no usable content;
		// resolve with an empty array so the TOC simply omits this block's headings.
		const content = (syncBlockInstance?.data?.content ?? []) as ADFEntity[];
		onResolve({ key: reference.localId, content });
	}, [isLoading, syncBlockInstance, reference.localId, onResolve]);

	return null;
};

type SubscribersProps = {
	manager: SyncBlockStoreManager | undefined;
	references: SyncBlockReference[];
	onEntry: (entry: SyncBlockTocEntry) => void;
};

export const SyncBlockTocSubscribers: FC<SubscribersProps> = ({ manager, references, onEntry }) => {
	if (!manager) {
		return null;
	}
	return (
		<>
			{references.map((reference) => (
				<SyncBlockTocSubscriber
					key={reference.localId}
					manager={manager}
					reference={reference}
					onResolve={onEntry}
				/>
			))}
		</>
	);
};
